import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { toastApiError } from "@/lib/toastApiError";
import type { Term, AdminStats } from "../types";
import {
  createTermMultipart,
  deleteTermRemote,
  fetchTermByIdRemote,
  fetchTermsCountRemote,
  fetchTermsRemote,
  termToMultipartFields,
  updateTermMultipart,
  type TermMultipartFields,
  type TermsListPagination,
} from "@/lib/termsApi";
import {
  normalizeForCompare,
  wordFirstLetter,
} from "@/lib/normalizeText";
import {
  TERMS_PAGE_SIZE_OPTIONS,
  TERMS_PAGE_SIZES,
  type TermsPageSizeOption,
} from "@/lib/termsPageSizes";

/** Fields sent via multipart PATCH/POST (`admin_notes` when supported by API). */
export type TermMultipartSave = Partial<TermMultipartFields> & {
  admin_notes?: string;
};

export type AdminTermsTableQuery = {
  page: number;
  pageSize: TermsPageSizeOption;
  search: string;
  statusFilter: string;
};

function apiStatusFromFilter(filter: string): string | undefined {
  if (filter === "all" || !filter.trim()) {
    return undefined;
  }
  if (filter === "rejected") {
    return "draft";
  }
  return filter;
}

function matchesAdminStatusFilter(term: Term, statusFilter: string): boolean {
  if (statusFilter === "all" || !statusFilter.trim()) {
    return true;
  }
  if (statusFilter === "rejected") {
    return term.status === "rejected";
  }
  return term.status === statusFilter;
}

/** Filter by status + multi-field substring search across word, meanings, contributor. */
export function filterAdminTermsByQuery(
  terms: Term[],
  query: Pick<AdminTermsTableQuery, "search" | "statusFilter">
): Term[] {
  let list = terms.filter(t => matchesAdminStatusFilter(t, query.statusFilter));
  const q = normalizeForCompare(query.search);
  if (q) {
    list = list.filter(t =>
      normalizeForCompare(t.word ?? "").includes(q) ||
      normalizeForCompare(t.word_gujarati ?? "").includes(q) ||
      normalizeForCompare(t.word_devanagari ?? "").includes(q) ||
      normalizeForCompare(t.word_hindi ?? "").includes(q) ||
      normalizeForCompare(t.contributor_name ?? "").includes(q) ||
      normalizeForCompare(t.contributor_email ?? "").includes(q) ||
      normalizeForCompare(t.meaning_english ?? "").includes(q)
    );
  }
  return list.sort(compareTermsByWordAsc);
}

function paginateAdminTerms(
  terms: Term[],
  page: number,
  pageSize: number
): { terms: Term[]; pagination: TermsListPagination } {
  const safePage = Math.max(1, page);
  const totalCount = terms.length;
  const start = (safePage - 1) * pageSize;
  return {
    terms: terms.slice(start, start + pageSize),
    pagination: {
      count: totalCount,
      page: safePage,
      page_size: pageSize,
    },
  };
}

export function useAdminTerms(isAdmin: boolean) {
  const { staffRole, isLexiconStaffAdmin } = useAuth();
  const canDeleteTerms =
    staffRole === "admin" || isLexiconStaffAdmin();

  const [terms, setTerms] = useState<Term[]>([]);
  const [tablePagination, setTablePagination] =
    useState<TermsListPagination | null>(null);
  const [isLoadingTerms, setIsLoadingTerms] = useState(true);
  const [tableQuery, setTableQuery] = useState<AdminTermsTableQuery>({
    page: 1,
    pageSize: TERMS_PAGE_SIZES.browseDefault,
    search: "",
    statusFilter: "all",
  });

  const [stats, setStats] = useState<AdminStats>({
    pending: 0,
    approved: 0,
    rejected: 0,
  });

  const [allTerms, setAllTerms] = useState<Term[]>([]);
  const [allTermsLoaded, setAllTermsLoaded] = useState(false);
  const [isLoadingAllTerms, setIsLoadingAllTerms] = useState(false);
  /** Avoid re-fetch loops when `isLoadingAllTerms` toggles or a request fails (e.g. 401). */
  const allTermsLoadRef = useRef<"idle" | "loading" | "loaded" | "failed">(
    "idle"
  );

  const fetchTermDetail = useCallback(async (termId: string) => {
    return fetchTermByIdRemote(termId, "staff");
  }, []);

  const fetchStats = useCallback(async () => {
    const [pending, approved, rejected] = await Promise.all([
      fetchTermsCountRemote("staff", "pending"),
      fetchTermsCountRemote("staff", "approved"),
      fetchTermsCountRemote("staff", "draft"),
    ]);
    const err =
      pending.error ?? approved.error ?? rejected.error ?? null;
    if (err) {
      console.error("Admin stats count:", err);
    }
    setStats({
      pending: pending.count,
      approved: approved.count,
      rejected: rejected.count,
    });
  }, []);

  const fetchTablePage = useCallback(
    async (query: AdminTermsTableQuery) => {
      setIsLoadingTerms(true);
      try {
        const { terms: rows, error, pagination } = await fetchTermsRemote(
          "staff",
          {
            page: query.page,
            page_size: query.pageSize,
            status: apiStatusFromFilter(query.statusFilter),
          }
        );
        if (error) {
          toastApiError(`Failed to load terms: ${error}`);
          console.error(error);
          setTerms([]);
          setTablePagination(null);
        } else {
          setTerms([...rows].sort(compareTermsByWordAsc));
          setTablePagination(pagination);
        }
      } catch (err) {
        console.error("Fetch terms error:", err);
        toastApiError("Network error fetching terms");
        setTerms([]);
        setTablePagination(null);
      } finally {
        setIsLoadingTerms(false);
      }
    },
    []
  );

  const fetchAllTermsForReports = useCallback(async (force = false) => {
    const phase = allTermsLoadRef.current;
    if (!force && (phase === "loading" || phase === "loaded" || phase === "failed")) {
      return;
    }
    allTermsLoadRef.current = "loading";
    setIsLoadingAllTerms(true);
    try {
      const { terms: rows, error } = await fetchTermsRemote("staff", {
        fetchAllPages: true,
        page_size: TERMS_PAGE_SIZES.fetchAllChunk,
      });
      if (error) {
        console.error("Fetch all terms:", error);
        allTermsLoadRef.current = "failed";
        setAllTermsLoaded(false);
      } else {
        setAllTerms([...rows].sort(compareTermsByWordAsc));
        setAllTermsLoaded(true);
        allTermsLoadRef.current = "loaded";
      }
    } catch (err) {
      console.error("Fetch all terms error:", err);
      allTermsLoadRef.current = "failed";
      setAllTermsLoaded(false);
    } finally {
      setIsLoadingAllTerms(false);
    }
  }, []);

  const syncTableFromCatalog = useCallback(
    (query: AdminTermsTableQuery) => {
      if (!allTermsLoaded) {
        setIsLoadingTerms(isLoadingAllTerms || allTermsLoadRef.current === "loading");
        setTerms([]);
        setTablePagination(null);
        return;
      }
      const filtered = filterAdminTermsByQuery(allTerms, query);
      const { terms: pageTerms, pagination } = paginateAdminTerms(
        filtered,
        query.page,
        query.pageSize
      );
      setTerms(pageTerms);
      setTablePagination(pagination);
      setIsLoadingTerms(false);
    },
    [allTerms, allTermsLoaded, isLoadingAllTerms]
  );

  const fetchTerms = useCallback(async () => {
    await fetchStats();
    // Always refresh the full catalog so the table re-sorts globally after any mutation.
    // The useEffect below will call syncTableFromCatalog automatically once allTerms updates.
    await fetchAllTermsForReports(true);
  }, [fetchStats, fetchAllTermsForReports]);

  const setTablePage = useCallback((page: number) => {
    setTableQuery(q => ({ ...q, page: Math.max(1, page) }));
  }, []);

  const setTablePageSize = useCallback((pageSize: TermsPageSizeOption) => {
    setTableQuery(q => ({ ...q, pageSize, page: 1 }));
  }, []);

  const setTableSearch = useCallback((search: string) => {
    setTableQuery(q => ({ ...q, search, page: 1 }));
  }, []);

  const setTableStatusFilter = useCallback((statusFilter: string) => {
    setTableQuery(q => ({ ...q, statusFilter, page: 1 }));
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      return;
    }
    void fetchStats();
  }, [isAdmin, fetchStats]);

  useEffect(() => {
    if (!isAdmin) {
      return;
    }
    void fetchAllTermsForReports();
  }, [isAdmin, fetchAllTermsForReports]);

  useEffect(() => {
    if (!isAdmin) {
      allTermsLoadRef.current = "idle";
      setAllTerms([]);
      setAllTermsLoaded(false);
      return;
    }
    // Prefer the in-memory catalog once loaded — it covers all records and sorts globally.
    // Fall back to API page-fetch only while the catalog is still being fetched.
    if (allTermsLoaded) {
      syncTableFromCatalog(tableQuery);
    } else {
      void fetchTablePage(tableQuery);
    }
  }, [
    isAdmin,
    tableQuery,
    allTermsLoaded,
    fetchTablePage,
    syncTableFromCatalog,
  ]);

  const reportTerms = allTermsLoaded ? allTerms : terms;

  const alphabetCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    reportTerms
      .filter(t => t.status === "approved")
      .forEach(term => {
        const letter = wordFirstLetter(term.word);
        if (!letter) {
          return;
        }
        counts[letter] = (counts[letter] || 0) + 1;
      });
    return Object.entries(counts).sort(([a], [b]) => a.localeCompare(b));
  }, [reportTerms]);

  const recentTermsByMonth = useMemo(() => {
    const months: Record<string, number> = {};
    reportTerms.forEach(term => {
      const month = new Date(term.created_at).toLocaleDateString("en-US", {
        month: "short",
        year: "2-digit",
      });
      months[month] = (months[month] || 0) + 1;
    });
    return Object.entries(months)
      .slice(-6)
      .map(([month, count]) => ({ month, count }));
  }, [reportTerms]);

  const handleApprove = async (term: Term) => {
    const { ok, error } = await updateTermMultipart(term.id, {
      ...termToMultipartFields({ ...term, status: "approved" }),
      admin_notes: term.admin_notes ?? "",
    });
    if (!ok || error) {
      toastApiError(error || "Failed to approve term");
    } else {
      toast.success(`"${term.word}" approved!`);
      fetchTerms();
    }
  };

  const handleReject = async (term: Term) => {
    const { ok, error } = await updateTermMultipart(term.id, {
      ...termToMultipartFields({ ...term, status: "rejected" }),
      admin_notes: term.admin_notes ?? "",
    });
    if (!ok || error) {
      toastApiError(error || "Failed to reject term");
    } else {
      toast.success(`"${term.word}" rejected.`);
      fetchTerms();
    }
  };

  const handleDelete = async (term: Term) => {
    if (!canDeleteTerms) {
      toastApiError("You don't have access to delete terms.");
      return;
    }
    const { ok, error } = await deleteTermRemote(term.id);
    if (!ok || error) {
      toastApiError(error || "Failed to delete term");
    } else {
      toast.success("Term deleted!");
      fetchTerms();
    }
  };

  const handleEdit = async (
    id: string,
    data: TermMultipartSave,
    audioFile?: File | null
  ) => {
    const { ok, error } = await updateTermMultipart(id, data, audioFile);
    if (!ok || error) {
      toastApiError(error || "Failed to update term");
    } else {
      toast.success("Term updated!");
      fetchTerms();
    }
  };

  const handleAdd = async (
    data: TermMultipartSave,
    audioFile?: File | null
  ) => {
    const { ok, error } = await createTermMultipart(data, audioFile, "staff");
    if (!ok || error) {
      toastApiError(error || "Failed to add term");
    } else {
      toast.success("Term added!");
      fetchTerms();
    }
  };

  return {
    terms,
    tablePagination,
    tableQuery,
    setTablePage,
    setTablePageSize,
    setTableSearch,
    setTableStatusFilter,
    isLoadingTerms,
    stats,
    alphabetCounts,
    recentTermsByMonth,
    reportTerms,
    allTermsLoaded,
    isLoadingAllTerms,
    fetchTerms,
    fetchTermDetail,
    handleApprove,
    handleReject,
    handleDelete,
    handleEdit,
    handleAdd,
    canDeleteTerms,
    pageSizeOptions: TERMS_PAGE_SIZE_OPTIONS,
  };
}
function compareTermsByWordAsc(a: Term, b: Term): number {
  const byWord = normalizeForCompare(a.word ?? "").localeCompare(
    normalizeForCompare(b.word ?? ""),
    undefined,
    { sensitivity: "base", numeric: true }
  );
  if (byWord !== 0) {
    return byWord;
  }
  const ta = Date.parse(a.created_at) || 0;
  const tb = Date.parse(b.created_at) || 0;
  return tb - ta;
}

